import { useEffect, useMemo, useState } from "react";
import { useCustom } from "@refinedev/core";
import type { TenantTheme } from "../config/theme";
import { useTenantThemeContext } from "../contexts/color-mode";
import { TENANT_SETTINGS_KEY } from "../authProvider";
import type { ITenantBusinessHours } from "../models/tenant";
import { getOpeningStatus, type OpeningStatus } from "../shared/openingHours";

export const useTenantTheme = (): void => {
    const { setTenantTheme } = useTenantThemeContext();

    // Read once on mount
    useEffect(() => {
        try {
            const settings = localStorage.getItem(TENANT_SETTINGS_KEY);
            if (settings) {
                const parsed = JSON.parse(settings);
                if (parsed?.theme && typeof parsed.theme === "object") {
                    setTenantTheme(parsed.theme as TenantTheme);
                }
            }
        } catch (error) {
            console.error("[useTenantTheme] Error:", error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount
};

export interface TenantLogos {
    logoLight?: string;
    logoDark?: string;
}

const TENANT_LOGOS_CACHE_KEY = "tenant_logos_cache";
const TENANT_LOGOS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface LogosCache {
    logos: TenantLogos | null;
    timestamp: number;
}

export const useTenantLogos = (apiUrl?: string): { logos: TenantLogos | null; isLoading: boolean } => {
    const [logos, setLogos] = useState<TenantLogos | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchLogos = async () => {
            // Check cache first
            try {
                const cached = localStorage.getItem(TENANT_LOGOS_CACHE_KEY);
                if (cached) {
                    const parsedCache: LogosCache = JSON.parse(cached);
                    if (Date.now() - parsedCache.timestamp < TENANT_LOGOS_CACHE_DURATION) {
                        setLogos(parsedCache.logos);
                        setIsLoading(false);
                        return;
                    }
                }
            } catch (e) {
                // Ignore cache errors
            }

            // Determine API URL - use provided or fallback to env variable
            const baseUrl = apiUrl || import.meta.env.VITE_API_URL || "";
            
            try {
                const response = await fetch(`${baseUrl}/tenants/logos`);
                if (response.ok) {
                    const data = await response.json();
                    setLogos(data);
                    // Cache the result
                    localStorage.setItem(TENANT_LOGOS_CACHE_KEY, JSON.stringify({
                        logos: data,
                        timestamp: Date.now(),
                    }));
                }
            } catch (error) {
                console.error("[useTenantLogos] Failed to fetch tenant logos:", error);
            }
            
            setIsLoading(false);
        };

        fetchLogos();
    }, [apiUrl]);

    return { logos, isLoading };
};

/**
 * Get the appropriate logo based on the current color mode
 */
export const getLogoForMode = (logos: TenantLogos | null, mode: 'light' | 'dark'): string | null => {
    if (!logos) return null;
    
    if (mode === "dark" && logos.logoDark) {
        return logos.logoDark;
    }
    if (mode === "light" && logos.logoLight) {
        return logos.logoLight;
    }
    // Fallback: use any available tenant logo
    return logos.logoDark || logos.logoLight || null;
};

/**
 * Fired by the opening-hours admin form after a successful save so the header
 * pill updates without a reload. Mirrors the existing `tenantSettingsUpdated`
 * channel used by the general settings form.
 */
export const TENANT_BUSINESS_HOURS_UPDATED_EVENT = "tenantBusinessHoursUpdated";

/**
 * Opening hours and closures of the caller's tenant. React Query dedupes this
 * across the header and the order page, so it costs one request per 5 minutes.
 */
export const useTenantBusinessHours = () => {
    const { data, isLoading, refetch } = useCustom<ITenantBusinessHours>({
        url: "tenants/business-hours",
        method: "get",
        queryOptions: {
            retry: false,
            staleTime: 5 * 60 * 1000,
        },
    });

    useEffect(() => {
        const handleUpdate = () => {
            refetch();
        };
        window.addEventListener(TENANT_BUSINESS_HOURS_UPDATED_EVENT, handleUpdate);
        return () => {
            window.removeEventListener(TENANT_BUSINESS_HOURS_UPDATED_EVENT, handleUpdate);
        };
    }, [refetch]);

    return {
        openingHours: data?.data?.openingHours ?? null,
        closingDates: data?.data?.closingDates ?? null,
        isLoading,
    };
};

/**
 * Open/closed verdict for the current tenant, recomputed every minute so the
 * badge flips at closing time without a navigation.
 */
export const useOpeningStatus = (): {
    status: OpeningStatus | null;
    openingHours: ITenantBusinessHours["openingHours"];
    closingDates: ITenantBusinessHours["closingDates"];
    isLoading: boolean;
} => {
    const { openingHours, closingDates, isLoading } = useTenantBusinessHours();
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!openingHours?.enabled) return;
        const interval = setInterval(() => setTick((value) => value + 1), 60 * 1000);
        return () => clearInterval(interval);
    }, [openingHours?.enabled]);

    const status = useMemo(
        () => getOpeningStatus(openingHours, closingDates),
        // `tick` is the intentional recompute trigger — the schedule itself rarely changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [openingHours, closingDates, tick],
    );

    return { status, openingHours, closingDates, isLoading };
};

/**
 * Hook to get tenant settings from localStorage
 */
export const useTenantSettings = (): Record<string, any> | null => {
    const [settings, setSettings] = useState<Record<string, any> | null>(null);

    useEffect(() => {
        const loadSettings = () => {
            try {
                const stored = localStorage.getItem(TENANT_SETTINGS_KEY);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    setSettings(parsed);
                }
            } catch (error) {
                console.error("[useTenantSettings] Error:", error);
                setSettings(null);
            }
        };

        loadSettings();

        // Listen for settings updates
        const handleSettingsUpdate = () => {
            loadSettings();
        };

        window.addEventListener('tenantSettingsUpdated', handleSettingsUpdate);
        return () => {
            window.removeEventListener('tenantSettingsUpdated', handleSettingsUpdate);
        };
    }, []);

    return settings;
};
